import React, { useEffect, useMemo, useState } from 'react';
import { Spin, Space, Alert, Popover, Button, Tabs } from '@bedrock/components';
import { AlertCircleFilled, ArrowCircleUpFilled, Edit, HelpCircle, ChevronRight } from '@bedrock/icons-react';
import classNames from 'classnames';
import { localPx2rem } from '@mpc/common/src/utils';
import PageFooter from '@mpc/common/src/features/PageFooter';
import useLayoutContentWidth from '@mpc/common/src/hooks/useLayoutContentWidth';
import { PathershipInfoModifyButton } from '@mpc/common/src/features/SupplierNew/EditEntry';
import { ArchiveContent, DeviceInfoContent } from '@mpc/common/src/features/SupplierNew/DetailExternal';
import { getVendorStorage } from '@mpc/common/src/utils/vendorStorage';
import AccidentList from '../../Accident/List';
import { getCurSupplier } from '../../../Main/MPCLayout/utils';
import Rfi_icon from '@/images/rfi-icon.png';
import { useRequest } from 'ahooks';
import { PartnershipRequestInvoker, supplierEnums } from '@purchase/supplier';
import { formatSupplierDetailExternal } from '@mpc/common/src/features/SupplierNew/DetailExternal/format';
import './index.less';

const viewClass = 'supplier-view';

type PartnerShipSimpleDetailDto = {
  /** 是否入库 */
  onboarded?: boolean;
  /** oa流程实例Id */
  oaNo?: string;
  vendorProcessAuditStatus?: string;
};

type SupplierDetailProps = {
  isFullscreen?: boolean;
  history?: any;
  location?: any;
  vendorProcessAudit?: PartnerShipSimpleDetailDto;
  account?: any;
  suppliersList?: any;
};

const SupplierDetail = (props: SupplierDetailProps) => {
  const { isFullscreen, history, location, vendorProcessAudit, account, suppliersList } = props;

  // 从 URL hash 恢复 Tab 状态（如 #accident），使返回后停留在事故管理
  const defaultActiveKey = (() => {
    const hash = location?.hash?.replace(/^#/, '') || '';
    return hash || 'archive';
  })();
  const [activeTab, setActiveTab] = useState(defaultActiveKey);

  // 导航前写入 #accident 到当前 URL，使 goBack 后 Tab 不丢失
  const navigateWithTabState = React.useCallback(
    (pathname: string) => {
      history.replace({ pathname: location.pathname, search: location.search, hash: '#accident' });
      history.push({ pathname });
    },
    [history, location],
  );

  // 首次挂载消费 hash 后立即清除，避免 hash 残留影响后续行为
  useEffect(() => {
    if (location?.hash) {
      const timer = requestAnimationFrame(() => {
        history.replace({ pathname: location.pathname, search: location.search, hash: '' });
      });
      return () => cancelAnimationFrame(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { vendorId, tenantCode } = getVendorStorage();
  const { vendorName } = getCurSupplier(suppliersList);
  const layoutContentWidth = useLayoutContentWidth();
  const styles = {
    width: isFullscreen ? '100vw' : layoutContentWidth + 'px',
    left: isFullscreen ? 0 : window.innerWidth - layoutContentWidth + 'px',
    top: isFullscreen ? 0 : localPx2rem(48),
  };

  const { data: vendorDetailData, loading } = useRequest(async () => {
    const res = await PartnershipRequestInvoker.getInstance().partnerShipOpenApiVendorVendorDetail({ vendorId });
    return formatSupplierDetailExternal(res?.data);
  });

  const shouldShowRFIButton = useMemo(() => {
    if (!vendorDetailData) return false;
    return vendorDetailData?.extendAttributeInfo?.hasCompletedWholeModification !== true;
  }, [vendorDetailData]);

  /** 设备信息 Tab 显示条件：tenantCode='04' 且 vendorStatus='02' */
  const showDeviceInfo = useMemo(() => {
    return tenantCode === '04' && vendorDetailData?.vendorStatus === '02';
  }, [tenantCode, vendorDetailData?.vendorStatus]);

  const alertTitle = useMemo(() => {
    const obj: Record<string, string> = {
      [supplierEnums.VendorProcessAuditStatusEnum.BOARDING_AUDIT]: '以下注册信息已提交,正在审批中，请耐心等待！',
      [supplierEnums.VendorProcessAuditStatusEnum.MODIFY_AUDIT]:
        '当前展示内容为更改前的信息，信息正在审批中，请耐心等待',
      [supplierEnums.VendorProcessAuditStatusEnum.OTHER_TENANT_MODIFY_AUDIT]:
        '当前供应商公共档案信息审批中，请耐心等待，如有疑问，请联系商务对接人',
    };
    return obj[vendorDetailData?.vendorProcessAuditStatus] || '';
  }, [vendorDetailData?.vendorProcessAuditStatus]);

  const alertIcon = useMemo(() => {
    return <AlertCircleFilled />;
  }, [vendorDetailData?.vendorProcessAuditStatus]);

  return (
    <Spin.Global
      spinning={false}
      className={classNames(viewClass, {
        'view-full-screen': isFullscreen,
      })}
      style={{
        ...styles,
      }}>
      <div className={classNames(`${viewClass}-wrapper`)}>
        <div className={`${viewClass}-header`}>
          <div className={`${viewClass}-header-top`}>
            <div className={`${viewClass}-header-left`}>
              <Space size={16}>
                <span className={`${viewClass}-header-left-span`}>{vendorName}</span>
              </Space>
            </div>
            <div className={`${viewClass}-header-right`}>
              <PathershipInfoModifyButton
              vendorId={vendorId}
              tenant={{ tenantCode: tenantCode }}
              vendorModifyType={supplierEnums.VendorModifyTypeEnum.INIT}
              type="checked"
              icon={<></>}
              onClick={(params) => {
                if (!params?.oaNo) {
                  return;
                }
                history.push({
                  ...location,
                  pathname: `${location.pathname}/editNew`,
                  search: `?oaNo=${params?.oaNo}&headerTitle=infoChange`,
                });
              }}>
              信息变更
            </PathershipInfoModifyButton>
              {shouldShowRFIButton && (
                <PathershipInfoModifyButton
                  vendorId={vendorId}
                  // RFI vendorModifyType传02，其他传01或者不传
                  vendorModifyType={supplierEnums.VendorModifyTypeEnum.COMPLETION}
                  type="primary"
                  tenant={{ tenantCode: tenantCode }}
                  icon={<Edit />}
                  onClick={(params) => {
                    if (!params?.oaNo) {
                      return;
                    }
                    history.push({
                      ...location,
                      pathname: `${location.pathname}/editNew`,
                      search: `?oaNo=${params?.oaNo}&headerTitle=RFI`,
                    });
                  }}>
                  信息完善 (RFI)
                  <Popover
                    content={
                      <div className="rfi-popover">
                        <div>
                          RFI（Request for Information）是平台为保障合作规范顺畅发起的信息完善需求，请尽快完成企业核心资料更新。
                        </div>
                        <Button
                          type="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(
                              'https://docs.popo.netease.com/lingxi/c0677a1777684d62ae2db44478ffc827',
                              '_blank',
                            );
                          }}
                          suffixIcon={<ChevronRight />}>
                          信息填写指引
                        </Button>
                      </div>
                    }>
                    <HelpCircle className="rfi-help" />
                  </Popover>
                </PathershipInfoModifyButton>
              )}
            </div>
          </div>
          <Tabs activeKey={activeTab} onChange={setActiveTab} className={`${viewClass}-header-tabs`}>
            <Tabs.TabPane tab="档案信息" key="archive" />
            {showDeviceInfo && <Tabs.TabPane tab="设备信息" key="deviceInfo" />}
            <Tabs.TabPane tab="事故管理" key="accident" />
          </Tabs>
        </div>
        <div className={`${viewClass}-body`}>
          {activeTab === 'archive' && (
            <>
              {loading ? (
                <Spin />
              ) : (
                <ArchiveContent
                  vendorDetailData={vendorDetailData}
                  vendorId={vendorId}
                  renderReminder={() => (
                    <>
                      {Boolean(alertTitle) && (
                        <Alert message={alertTitle} type="info" className="mb-[16px]" icon={alertIcon} showIcon />
                      )}
                      {shouldShowRFIButton && (
                        <div className="mb-[16px] supplier-info-message">
                          <div className="supplier-info-message-content">
                            <div className="supplier-info-message-header">
                              <div className="supplier-info-message-icon">
                                <AlertCircleFilled />
                              </div>
                              <div className="supplier-info-message-text">
                                重要提醒：请立即点击右侧
                                <span className="highlight-link">【信息完善 (RFI)】</span>
                                按钮，完成企业资质、组织/人员信息、服务能力证明等核心信息的更新。
                                <Button
                                  type="text"
                                  size="small"
                                  className="supplier-info-message-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open('https://docs.popo.netease.com/lingxi/c0677a1777684d62ae2db44478ffc827', '_blank');
                                  }}
                                  suffixIcon={<ChevronRight />}>
                                  信息填写指引
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div className="supplier-info-message-points">
                            {['规范合作流程', '提升合作对接效率', '精准匹配合作需求'].map((point, index) => (
                              <div key={index} className="supplier-info-point">
                                <span className="point-dot">•</span>
                                <span>{point}</span>
                              </div>
                            ))}
                          </div>
                          <div className="supplier-info-message-image">
                            <img src={Rfi_icon} alt="" />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                />
              )}
            </>
          )}
          {activeTab === 'deviceInfo' && showDeviceInfo && <DeviceInfoContent vendorId={vendorId} />}
          {activeTab === 'accident' && (
            <AccidentList
              vendorId={vendorId}
              onViewDetail={(accidentId) =>
                navigateWithTabState(`${location.pathname}/accident/detail/${accidentId}`)
              }
              onProcess={(oaNo) =>
                navigateWithTabState(`${location.pathname}/accident/oaFlow/${oaNo}`)
              }
            />
          )}
          <PageFooter />
        </div>
      </div>
    </Spin.Global>
  );
};

export default SupplierDetail;
